/**
 * ConfettiCelebration — animated confetti/particle burst for milestone achievements.
 *
 * Used when Wouter reaches a bonus milestone (Bronze/Silver/Gold) or earns
 * an achievement badge. Renders particles that animate from center outward
 * and fade over a few seconds.
 *
 * Add data-celebration="milestone" or data-celebration="badge" to target elements
 * for future JS celebration hooks.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CONFETTI_COLORS = [
  '#0F4C75', '#1B9AAA', '#F4A261', '#2EC4B6',
  '#C9973A', '#E76F51', '#FFD700', '#FF6B6B',
  '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
];

const PARTICLE_COUNT = 50;

/**
 * Generate random confetti particles.
 */
function generateParticles(count) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: (Math.random() - 0.5) * 600,
    y: (Math.random() - 0.5) * 600 - 200,
    rotation: Math.random() * 720 - 360,
    scale: Math.random() * 0.8 + 0.4,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    shape: Math.random() > 0.5 ? 'circle' : 'rect',
    delay: Math.random() * 0.3,
    duration: 1.5 + Math.random() * 1.5,
  }));
}

/**
 * @param {boolean} show — whether to display the celebration
 * @param {Function} onComplete — callback when animation finishes
 * @param {string} type — 'confetti' | 'burst' (default: 'confetti')
 */
export default function ConfettiCelebration({ show, onComplete, type = 'confetti' }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (show) {
      setParticles(generateParticles(PARTICLE_COUNT));
      const timer = setTimeout(() => {
        if (onComplete) onComplete();
      }, 3500);
      return () => clearTimeout(timer);
    } else {
      setParticles([]);
    }
  }, [show, onComplete]);

  if (!show) return null;

  return (
    <div style={styles.overlay} data-celebration="confetti">
      <AnimatePresence>
        {particles.map((p) => (
          <motion.div
            key={p.id}
            style={{
              ...styles.particle,
              background: p.color,
              borderRadius: p.shape === 'circle' ? '50%' : '2px',
              width: p.shape === 'circle' ? '10px' : `${8 + Math.random() * 8}px`,
              height: p.shape === 'circle' ? '10px' : `${4 + Math.random() * 4}px`,
            }}
            initial={{
              x: 0,
              y: 0,
              scale: 0,
              rotate: 0,
              opacity: 1,
            }}
            animate={{
              x: p.x,
              y: p.y,
              scale: p.scale,
              rotate: p.rotation,
              opacity: [1, 1, 0],
            }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

/**
 * MilestoneCelebration — full-screen celebration overlay for bonus tier milestones.
 *
 * Shows confetti + milestone details (tier, emoji, amount) with a satisfying
 * spring animation entrance.
 */
export function MilestoneCelebration({ show, milestone, onDismiss }) {
  const [showConfetti, setShowConfetti] = useState(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (show) {
      // Trigger confetti slightly after the card appears
      const confettiTimer = setTimeout(() => setShowConfetti(true), 300);
      // Auto-dismiss after 8 seconds as a safety net
      const autoDismissTimer = setTimeout(() => {
        if (onDismissRef.current) onDismissRef.current();
      }, 8000);
      return () => { clearTimeout(confettiTimer); clearTimeout(autoDismissTimer); };
    }
    setShowConfetti(false);
  }, [show]);

  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
  }, []);

  if (!show || !milestone) return null;

  return (
    <div style={styles.milestoneOverlay} onClick={onDismiss} data-celebration="milestone">
      <ConfettiCelebration show={showConfetti} onComplete={handleConfettiComplete} />

      <motion.div
        style={styles.milestoneCard}
        initial={{ opacity: 0, scale: 0.3, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ type: 'spring', stiffness: 180, damping: 15 }}
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 150, delay: 0.2 }}
          style={styles.milestoneEmojiWrap}
        >
          <span style={styles.milestoneEmoji}>{milestone.emoji}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 style={styles.milestoneTitle}>Milestone Reached!</h2>
          <h3 style={styles.milestoneName}>{milestone.label}</h3>
          <p style={styles.milestoneAmount}>
            {milestone.amount > 0 ? `R${milestone.amount.toLocaleString('en-ZA')} bonus earned` : 'Congratulations!'}
          </p>
          <p style={styles.milestoneDesc}>
            {milestone.count} qualifying files completed this month.
          </p>
        </motion.div>

        <motion.button
          className="btn btn-primary"
          style={styles.milestoneBtn}
          onClick={onDismiss}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          Let's keep going!
        </motion.button>
      </motion.div>
    </div>
  );
}

/**
 * StatusChangeAnimation — a quick check/pulse animation shown when
 * a file status transition is confirmed.
 */
export function StatusChangeAnimation({ show, status, onComplete }) {
  const [fading, setFading] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (show) {
      setFading(false);
      // Start fade-out after 3 seconds
      const fadeTimer = setTimeout(() => setFading(true), 3000);
      // Remove from DOM after 3s display + 0.5s fade
      const removeTimer = setTimeout(() => {
        if (onCompleteRef.current) onCompleteRef.current();
      }, 3500);
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [show]);

  if (!show) return null;

  const messages = {
    in_progress: { text: "Let's do this!", icon: '🎯' },
    paused: { text: 'Clock paused', icon: '⏸️' },
    sent_to_sign: { text: 'Awaiting signature!', icon: '✅' },
    signed: { text: 'Signed & sent to New Business!', icon: '🖊️' },
    completed: { text: 'File complete!', icon: '🎉' },
  };

  const msg = messages[status] || { text: 'Updated!', icon: '✅' };

  return (
    <motion.div
      style={styles.statusAnim}
      initial={{ opacity: 0, scale: 0.8, y: 10 }}
      animate={fading ? { opacity: 0, scale: 0.8 } : { opacity: 1, scale: 1, y: 0 }}
      transition={fading ? { duration: 0.5 } : { type: 'spring', stiffness: 200, damping: 15 }}
    >
      <motion.span
        style={styles.statusAnimIcon}
        initial={{ scale: 0 }}
        animate={{ scale: [0, 1.3, 1] }}
        transition={{ duration: 0.5 }}
      >
        {msg.icon}
      </motion.span>
      <span style={styles.statusAnimText}>{msg.text}</span>
    </motion.div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 3000,
    overflow: 'hidden',
  },
  particle: {
    position: 'absolute',
  },
  milestoneOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2500,
    padding: '1rem',
  },
  milestoneCard: {
    position: 'relative',
    background: 'white',
    borderRadius: '24px',
    padding: '3rem 2.5rem 2rem',
    textAlign: 'center',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
    zIndex: 2,
  },
  milestoneEmojiWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(244, 162, 97, 0.15), rgba(201, 151, 58, 0.15))',
    marginBottom: '1rem',
  },
  milestoneEmoji: {
    fontSize: '3rem',
  },
  milestoneTitle: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: 'var(--cp-accent)',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    marginBottom: '0.3rem',
  },
  milestoneName: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: 'var(--cp-text)',
    marginBottom: '0.3rem',
  },
  milestoneAmount: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#0F4C75',
    marginBottom: '0.25rem',
  },
  milestoneDesc: {
    fontSize: '0.85rem',
    color: '#6B7A8D',
    marginBottom: '0.5rem',
  },
  milestoneBtn: {
    marginTop: '1rem',
    padding: '0.75rem 2rem',
    fontSize: '0.95rem',
  },
  statusAnim: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1.5rem 2rem',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
    zIndex: 2000,
    pointerEvents: 'none',
    cursor: 'default',
    userSelect: 'none',
  },
  statusAnimIcon: {
    fontSize: '2.5rem',
    cursor: 'default',
    userSelect: 'none',
  },
  statusAnimText: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    cursor: 'default',
    userSelect: 'none',
  },
};
